import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Truck, ChefHat, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/Layout';

const HomePage = () => {

  const features = [
    {
      icon: ChefHat,
      title: 'Cuisine Fusion',
      description: 'Saveurs exotiques de Maurice et tradition française',
    },
    {
      icon: Truck,
      title: 'Livraison Rapide',
      description: 'Dijon & Gevrey-Chambertin en 30-60 min',
    },
    {
      icon: Clock,
      title: 'Sur Commande',
      description: 'Fraîcheur garantie, préparé à la demande',
    },
  ];

  const featuredDishes = [
    {
      name: 'Curry Poulet Lait de Coco',
      description: 'Sauce crémeuse au lait de coco',
      price: '10.90 €',
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_maurice-flavors/artifacts/0jlss9gs_C16536EA-FF88-4199-95CA-8C6450BA435E.PNG',
    },
    {
      name: 'Rougail Saucisses',
      description: 'Sauce tomate épicée, riz',
      price: '9.90 €',
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_maurice-flavors/artifacts/ud6z4xkc_IMG_9838.webp',
    },
    {
      name: 'Tagliatelles Carbonara',
      description: 'Lardons, crème, parmesan',
      price: '9.90 €',
      image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_maurice-flavors/artifacts/2rpk9svw_A3CF107D-02D1-401B-9F1A-34A9D2F2BC20.jpg',
    },
  ];

  return (
    <Layout>
      {/* Hero Section - Modern */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background with overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_maurice-flavors/artifacts/0jlss9gs_C16536EA-FF88-4199-95CA-8C6450BA435E.PNG"
            alt="L'Île Gourmande"
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 animate-fade-in">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-white/90 text-sm font-medium">Cuisine Mauricienne & Française</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 animate-slide-up leading-tight">
              L'Île<br />
              <span className="text-primary">Gourmande</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed animate-slide-up stagger-1 max-w-lg">
              Une fusion unique de saveurs exotiques et de tradition culinaire. 
              Livraison du jeudi au dimanche.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-2">
              <Link to="/menu">
                <Button 
                  size="lg" 
                  className="rounded-full bg-primary hover:bg-primary/90 px-8 h-14 text-base shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5"
                  data-testid="hero-cta-menu"
                >
                  Commander maintenant
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/menu">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full border-white/30 text-white hover:bg-white/10 px-8 h-14 text-base backdrop-blur-sm"
                  data-testid="hero-cta-menu-2"
                >
                  Voir la carte
                </Button>
              </Link>
            </div>

            {/* Delivery badge */}
            <div className="mt-12 inline-flex items-center gap-3 px-5 py-3 bg-white/10 backdrop-blur-md rounded-2xl animate-fade-in stagger-3">
              <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-white/60 text-xs">Zone de livraison</p>
                <p className="text-white font-medium">Dijon & Gevrey-Chambertin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section - Modern cards */}
      <section className="py-20 md:py-28 bg-background relative overflow-hidden" data-testid="features-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.05),transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group p-8 rounded-3xl bg-white border border-stone-100 hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <feature.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Dishes Section - Modern grid */}
      <section className="py-20 md:py-28 bg-muted/50" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
              Nos spécialités
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold">
              Plats Signature
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredDishes.map((dish, index) => (
              <div
                key={dish.name}
                className="group relative rounded-3xl overflow-hidden bg-white shadow-sm hover:shadow-2xl transition-all duration-500"
                data-testid={`featured-dish-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={dish.image}
                    alt={dish.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-xl font-semibold">{dish.name}</h3>
                    <span className="text-primary font-bold text-lg">{dish.price}</span>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">{dish.description}</p>
                  <div className="flex items-center gap-2 text-xs text-accent font-medium">
                    <Sparkles className="h-3.5 w-3.5" />
                    Fait maison
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/menu">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-10 h-14 text-base shadow-lg shadow-primary/20"
                data-testid="view-full-menu"
              >
                Voir toute la carte
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Snacks Preview */}
      <section className="py-20 md:py-28 bg-background" data-testid="snacks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
                Snacks
              </span>
              <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
                Nos Encas Croustillants
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Samoussas, nems et poulet croustillant préparés à la commande. 
                Parfaits pour un apéritif ou pour accompagner vos plats.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between p-4 bg-muted rounded-2xl">
                  <span className="font-medium">Samoussas (10 pièces)</span>
                  <span className="text-primary font-bold">6.50 €</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted rounded-2xl">
                  <span className="font-medium">Nems (10 pièces)</span>
                  <span className="text-primary font-bold">6.50 €</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted rounded-2xl">
                  <span className="font-medium">Poulet Croustillant (10 pièces)</span>
                  <span className="text-primary font-bold">7.50 €</span>
                </div>
              </div>

              <Link to="/menu">
                <Button variant="outline" className="rounded-full px-8 h-12">
                  Voir les snacks
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_maurice-flavors/artifacts/wc2p6i7j_IMG_8177.jpeg"
                  alt="Samoussas"
                  className="rounded-3xl shadow-lg w-full aspect-square object-cover"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_maurice-flavors/artifacts/oeluj24t_IMG_8180.jpeg"
                  alt="Nems"
                  className="rounded-3xl shadow-lg w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="pt-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_maurice-flavors/artifacts/w9rd842e_IMG_8178.jpeg"
                  alt="Poulet Croustillant"
                  className="rounded-3xl shadow-lg w-full aspect-[3/4] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Zones Section - Modern */}
      <section className="py-20 md:py-28 bg-muted/50" data-testid="delivery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
                  Livraison
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
                  Zones de Livraison
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                  Nous livrons vos plats directement chez vous, 
                  préparés avec amour et livrés avec soin.
                </p>

                <div className="space-y-4">
                  <div className="p-6 rounded-2xl bg-gradient-to-r from-accent/5 to-transparent border border-accent/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-lg">Gevrey-Chambertin</h4>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-accent">OFFERTE</span>
                        <p className="text-xs text-muted-foreground">dès 20€ de commande</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Gevrey, Marsannay, Nuits-St-Georges & villages</span>
                      <span>20-30 min • Min. 15 €</span>
                    </div>
                  </div>

                  <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/5 to-transparent border border-primary/10">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-lg">Dijon Sud</h4>
                      <span className="text-2xl font-bold text-primary">5.00 €</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Dijon, Chenôve, Longvic, Genlis, Beaune</span>
                      <span>30-45 min • Min. 20 €</span>
                    </div>
                  </div>

                  <div className="p-6 rounded-2xl bg-gradient-to-r from-stone-100 to-transparent border border-stone-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-lg">Dijon Centre / Nord</h4>
                      <span className="text-2xl font-bold text-stone-600">9.00 €</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Talant, Fontaine, Quetigny, Chevigny, Auxonne, Is-sur-Tille…</span>
                      <span>45-60 min • Min. 25 €</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-accent/20 rounded-[2rem] blur-2xl opacity-50" />
                <img
                  src="https://customer-assets.emergentagent.com/job_maurice-flavors/artifacts/pq38pzox_IMG_8175.jpeg"
                  alt="Mine Frit"
                  className="relative rounded-3xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Modern */}
      <section className="py-20 md:py-28 bg-secondary relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,hsl(var(--primary)/0.15),transparent_50%)]" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Prêt à découvrir nos saveurs ?
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Commandez en ligne et savourez des plats authentiques, 
            préparés avec des produits frais et des épices sélectionnées.
          </p>
          <Link to="/menu">
            <Button 
              size="lg" 
              className="rounded-full bg-primary hover:bg-primary/90 px-10 h-14 text-base shadow-lg shadow-primary/30"
              data-testid="cta-order-now"
            >
              Commander maintenant
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          
          <p className="mt-8 text-white/50 text-sm">
            Livraison : Jeu-Ven 19h-22h • Sam-Dim 11h-14h & 18h-22h
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
